import React, { useCallback, useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import {
  applyProspectPagesJaunes,
  searchProspectPagesJaunes,
} from '../../../api/prospects';

const GENERIC_ERROR = 'Une erreur est survenue.';

function isBlank(value) {
  const v = String(value ?? '').trim().toLowerCase();
  return !v || v === '-' || v === 'n/a' || v === 'non renseigné';
}

function hasValidPhone(contact) {
  const digits = String(contact?.phone || contact?.mobilePhone || '').replace(/\D/g, '');
  return digits.length >= 10;
}

function hasValidEmail(contact) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(contact?.email || '').trim());
}

export function needsPagesJaunesEnrichment(contact) {
  const company = String(contact?.company || '').trim();
  if (isBlank(company)) return false;
  return !hasValidPhone(contact) || !hasValidEmail(contact);
}

export function canEnrichProspect(contact) {
  const company = String(contact?.company || '').trim();
  return !isBlank(company);
}

export function isProspectEnriched(contact) {
  return Boolean(contact?.enrichedAt);
}

const PagesJaunesEnrichModal = ({ contact, onClose, onApplied, onEnrichmentAttempted }) => {
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [emptyResults, setEmptyResults] = useState(false);
  const [previewFields, setPreviewFields] = useState([]);
  const [candidate, setCandidate] = useState(null);

  const runSearch = useCallback(async () => {
    if (!contact?.id) return;
    setLoading(true);
    setError('');
    setEmptyResults(false);
    setPreviewFields([]);
    setCandidate(null);
    try {
      const res = await searchProspectPagesJaunes(contact.id);
      const fields = Array.isArray(res?.data?.fields) ? res.data.fields : [];
      const match = res?.data?.candidate || null;

      if (!match || fields.length === 0) {
        setEmptyResults(true);
        onEnrichmentAttempted?.(contact.id);
        return;
      }

      setPreviewFields(fields);
      setCandidate(match);
    } catch {
      setError(GENERIC_ERROR);
    } finally {
      setLoading(false);
    }
  }, [contact?.id]);

  useEffect(() => {
    runSearch();
  }, [runSearch]);

  const handleApply = async () => {
    if (!candidate || !contact?.id) return;
    setApplying(true);
    setError('');
    try {
      const res = await applyProspectPagesJaunes(contact.id, candidate);
      const p = res?.data?.prospect;
      if (p) {
        onApplied?.(contact.id, p);
      }
      onClose?.();
    } catch {
      setError(GENERIC_ERROR);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1200] p-4">
      <div className="bg-card rounded-xl shadow-elevation-3 w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="text-lg font-semibold text-foreground">
            Enrichir le prospect
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="Loader2" size={16} className="animate-spin" />
              Recherche d&apos;informations ODINT (Opendata Intelligence)…
            </div>
          )}

          {!loading && error && (
            <div className="rounded-lg border border-error/30 bg-error/5 p-3 text-sm text-error">
              {error}
            </div>
          )}

          {!loading && emptyResults && !error && (
            <p className="text-sm text-muted-foreground">
              Aucune information trouvée.
            </p>
          )}

          {!loading && !error && previewFields.length > 0 && (
            <>
              <p className="text-sm text-muted-foreground">
                Des informations ont été trouvées. Confirmez pour les ajouter au prospect.
              </p>
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="divide-y divide-border">
                  {previewFields.map((field) => (
                    <div
                      key={field.key}
                      className="px-3 py-2 text-sm flex justify-between gap-4"
                    >
                      <span className="text-muted-foreground">{field.label}</span>
                      <span className="text-foreground font-medium text-right">
                        {field.suggested}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="border-t border-border p-4 flex gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={onClose} disabled={applying}>
            Annuler
          </Button>
          <Button
            size="sm"
            onClick={handleApply}
            disabled={loading || applying || !candidate || previewFields.length === 0}
            loading={applying}
          >
            Confirmer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PagesJaunesEnrichModal;
